import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import jwt from "jwt-decode";

import user_service from "../service/user_service";
import Loader from "../../Pages/Loader/Loader.js";
import Toaster from "../../Pages/Toaster/Toaster";

export default function NewslettersAdminList() {
  const navigate = useNavigate();
  const [loader, setLoader] = useState({ isActive: null });
  const { isActive } = loader;

  const [toaster, setToaster] = useState({
    types: null,
    isShow: null,
    toasterBody: "",
    message: "",
  });
  const { types, isShow, toasterBody, message } = toaster;

  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);

  const isAdmin = useMemo(() => {
    try {
      return String(jwt(localStorage.getItem("auth"))?.contactType || "").toLowerCase() === "admin";
    } catch {
      return false;
    }
  }, []);

  const handleDelete = async (e, newsletterId) => {
    e.stopPropagation();
    if (!window.confirm("Delete this newsletter? This cannot be undone.")) return;

    setLoader({ isActive: true });
    try {
      await user_service.newslettersDelete(newsletterId);
      setToaster({
        types: "success",
        isShow: true,
        toasterBody: "Newsletter deleted.",
        message: "Deleted",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 1000);
      await fetchList();
    } catch (err) {
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: err?.response?.data?.message || "Failed to delete newsletter.",
        message: "Error",
      });
    } finally {
      setLoader({ isActive: false });
    }
  };

  const fetchList = async () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    const query = params.toString() ? `?${params.toString()}` : "";

    setLoader({ isActive: true });
    try {
      const res = await user_service.newslettersAdminList(query);
      setItems(res?.data?.data || []);
    } catch (e) {
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: e?.response?.data?.message || "Failed to load newsletters.",
        message: "Error",
      });
    } finally {
      setLoader({ isActive: false });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    if (isAdmin) fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  if (!isAdmin) return null;

  return (
    <div className="bg-secondary float-left w-100 pt-4">
      <Loader isActive={isActive} />
      {isShow && (
        <Toaster types={types} isShow={isShow} toasterBody={toasterBody} message={message} />
      )}
      <main className="page-wrapper">
        <div className="container pb-5">
          <div className="d-flex align-items-center justify-content-between mb-4">
            <h3 className="text-white mb-0">Newsletters</h3>
            <NavLink className="btn btn-primary" to="/control-panel/newsletters/new">
              Create newsletter
            </NavLink>
          </div>

          <div className="bg-light border rounded-3 p-3 mb-3">
            <div className="row g-2 align-items-end">
              <div className="col-md-3">
                <label className="form-label">Status</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="draft">Draft</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="sent">Sent</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Search</label>
                <input
                  className="form-control"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by title"
                />
              </div>
              <div className="col-md-3 d-flex gap-2">
                <button className="btn btn-secondary w-100" type="button" onClick={fetchList}>
                  Apply
                </button>
                <button
                  className="btn btn-outline-secondary w-100"
                  type="button"
                  onClick={() => {
                    setStatus("");
                    setQ("");
                    setTimeout(fetchList, 0);
                  }}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          <div className="bg-light border rounded-3 p-0 overflow-hidden">
            {items?.length ? (
              <div className="list-group list-group-flush">
                {items.map((n) => (
                  <div
                    key={n._id}
                    className="list-group-item list-group-item-action d-flex align-items-center justify-content-between"
                    role="button"
                    tabIndex={0}
                    onClick={() =>
                      navigate(
                        n.status === "sent"
                          ? `/control-panel/newsletters/view/${n._id}`
                          : `/control-panel/newsletters/edit/${n._id}`
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        navigate(
                          n.status === "sent"
                            ? `/control-panel/newsletters/view/${n._id}`
                            : `/control-panel/newsletters/edit/${n._id}`
                        );
                      }
                    }}
                  >
                    <div>
                      <div className="fw-semibold">{n.title}</div>
                      <div className="text-muted small">
                        {n.status?.toUpperCase()}
                        {n.sendAt ? ` · Send: ${new Date(n.sendAt).toLocaleString()}` : ""}
                        {n.sentAt ? ` · Sent: ${new Date(n.sentAt).toLocaleString()}` : ""}
                      </div>
                    </div>
                    <div className="d-flex align-items-center gap-3">
                      <div className="text-muted small">{n.visibleInArchive ? "Visible" : "Hidden"}</div>
                      {n.status !== "sent" ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          title="Delete newsletter"
                          onClick={(e) => handleDelete(e, n._id)}
                        >
                          <i className="fi-trash" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-3 text-muted">No newsletters found.</div>
            )}
          </div>

          <div className="mt-3">
            <button className="btn btn-secondary" type="button" onClick={() => navigate("/control-panel")}>
              Back to Control Panel
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}

